import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight, Search, BookOpen, Library, Sparkles, Sun, Moon, Languages } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { TIDS } from "@/constants/testIds";

function Counter({ value }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!value) return;
        let start = 0;
        const target = Number(value);
        const dur = 900;
        const step = Math.max(1, Math.ceil(target / (dur / 16)));
        const id = setInterval(() => {
            start += step;
            if (start >= target) { setN(target); clearInterval(id); }
            else setN(start);
        }, 16);
        return () => clearInterval(id);
    }, [value]);
    return <>{n.toLocaleString()}</>;
}

export default function Landing() {
    const { t, theme, setTheme, lang, setLang } = useApp();
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [query, setQuery] = useState("");

    useEffect(() => { api.stats().then(setStats).catch(() => {}); }, []);

    const onSearch = (e) => {
        e.preventDefault();
        nav(`/search${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`);
    };

    return (
        <div className="relative min-h-screen hero-gradient overflow-hidden">
            {/* top bar */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 sm:px-10 py-5">
                <div className="flex items-center gap-2.5">
                    <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center shadow-lg">
                        <GraduationCap className="h-5 w-5 text-white" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display font-bold text-base">{t.appName}</div>
                        <div className="text-[10px] text-muted-foreground">{t.appSubtitle}</div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        data-testid={TIDS.languageToggle}
                        onClick={() => setLang(lang === "en" ? "od" : "en")}
                        className="h-9 px-3 rounded-full glass text-xs font-semibold flex items-center gap-1.5 active:scale-95"
                    >
                        <Languages className="h-3.5 w-3.5" />
                        {t.switchLang}
                    </button>
                    <button
                        data-testid={TIDS.themeToggle}
                        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        className="h-9 w-9 rounded-full glass grid place-items-center active:scale-95"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            {/* floating shapes */}
            <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-secondary/20 blur-3xl" />
            <div className="pointer-events-none absolute top-1/2 -left-20 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-10 pt-28 sm:pt-32 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center max-w-3xl mx-auto"
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold mb-6">
                        <Sparkles className="h-3.5 w-3.5 text-accent" />
                        Classes 1–8 • Odia & English • PWA
                    </div>
                    <h1
                        data-testid={TIDS.heroTitle}
                        className={`font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-balance leading-[1.05] mb-5 ${lang === "od" ? "font-odia" : ""}`}
                    >
                        {t.heroTitle}
                    </h1>
                    <p className={`text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed ${lang === "od" ? "font-odia" : ""}`}>
                        {t.heroDesc}
                    </p>

                    <form onSubmit={onSearch} className="max-w-2xl mx-auto mb-8">
                        <div className="glass-strong rounded-full flex items-center gap-2 p-2 pl-5 shadow-xl">
                            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
                            <input
                                data-testid={TIDS.heroSearchInput}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t.searchPlaceholder}
                                className={`flex-1 bg-transparent outline-none text-sm sm:text-base placeholder:text-muted-foreground py-2 ${lang === "od" ? "font-odia" : ""}`}
                            />
                            <button
                                type="submit"
                                data-testid={TIDS.heroSearchBtn}
                                className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold active:scale-95 transition-transform flex items-center gap-1.5"
                            >
                                {t.searchNow}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </form>

                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <button
                            data-testid={TIDS.heroExploreBtn}
                            onClick={() => nav("/dashboard")}
                            className="rounded-full bg-foreground text-background px-6 py-3 text-sm font-semibold active:scale-95 transition-transform flex items-center gap-2"
                        >
                            <BookOpen className="h-4 w-4" />
                            {t.getStarted}
                        </button>
                        <button
                            onClick={() => nav("/classes")}
                            data-testid="hero-classes-btn"
                            className="rounded-full glass-strong px-6 py-3 text-sm font-semibold active:scale-95 transition-transform flex items-center gap-2"
                        >
                            <Library className="h-4 w-4" />
                            {t.exploreClasses}
                        </button>
                    </div>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto"
                >
                    <StatCard
                        tid={TIDS.statTotalClasses}
                        label={t.totalClasses}
                        value={stats?.total_classes}
                        gradient="from-primary to-secondary"
                    />
                    <StatCard
                        tid={TIDS.statTotalSubjects}
                        label={t.totalSubjects}
                        value={stats?.total_subjects}
                        gradient="from-accent to-emerald-500"
                    />
                    <StatCard
                        tid={TIDS.statTotalCodes}
                        label={t.totalLOCodes}
                        value={stats?.total_codes}
                        gradient="from-amber-500 to-rose-500"
                        className="col-span-2 md:col-span-1"
                    />
                </motion.div>
            </div>
        </div>
    );
}

function StatCard({ tid, label, value, gradient, className = "" }) {
    return (
        <div
            data-testid={tid}
            className={`relative overflow-hidden rounded-3xl bg-card border border-border/60 p-6 shadow-sm ${className}`}
        >
            <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${gradient} opacity-20 blur-2xl`} />
            <div className="relative">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">{label}</div>
                <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
                    {value !== undefined && value !== null ? <Counter value={value} /> : "—"}
                </div>
            </div>
        </div>
    );
}
